import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import {
  TrendingUp, Search, Tag, Users, Loader2, AlertCircle, RefreshCw,
  Plus, X, ExternalLink, ArrowDown, CheckCircle2, AlertTriangle,
} from "lucide-react";

function gbp(p) {
  if (p == null) return "—";
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const TABS = [
  { key: "traffic", label: "Live traffic", Icon: TrendingUp },
  { key: "funnel", label: "Funnel", Icon: Users },
  { key: "promo", label: "Promo codes", Icon: Tag },
  { key: "seo", label: "SEO audit", Icon: Search },
];

export default function AdminMarketingPanel() {
  const [tab, setTab] = React.useState("traffic");

  return (
    <section className="mb-16" data-testid="admin-marketing-section">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">Marketing</h2>
          <p className="mt-1 text-xs text-[#475569]">Traffic, conversion funnel, promo codes, and on-page SEO audit.</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-[#0A0A0A]/10 mb-6" role="tablist">
        {TABS.map((t) => {
          const I = t.Icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              data-testid={`marketing-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-bold border-b-2 transition-colors ${
                active ? "border-[#1E4D55] text-[#1E4D55]" : "border-transparent text-[#475569] hover:text-[#0A0A0A]"
              }`}
            >
              <I size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "traffic" && <LiveTrafficTab />}
      {tab === "funnel" && <FunnelTab />}
      {tab === "promo" && <PromoCodesTab />}
      {tab === "seo" && <SeoAuditTab />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Live traffic
// ---------------------------------------------------------------------------
function LiveTrafficTab() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/marketing/live-traffic");
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load traffic");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  if (loading && !data) return <Loader2 size={20} className="animate-spin text-[#1E4D55]" />;
  if (error) return <ErrorBox msg={error} />;
  if (!data) return null;
  const maxHits = Math.max(1, ...(data.top_pages || []).map((p) => p.hits));

  return (
    <div data-testid="traffic-panel">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 border-t border-l border-[#0A0A0A]/10 mb-6">
        <Stat label="Online now" value={data.online_now} accent />
        <Stat label="Hits last 24h" value={data.hits_24h} />
        <Stat label="Top pages" value={data.top_pages?.length || 0} />
      </div>

      <h3 className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mb-3">Top pages (last 24h)</h3>
      <div className="space-y-2" data-testid="traffic-top-pages">
        {data.top_pages?.map((p) => (
          <div key={p.path} className="bg-white border border-[#0A0A0A]/10 p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-[#0A0A0A] truncate">{p.path}</span>
              <span className="text-xs text-[#475569] shrink-0">{p.hits} hits · {p.unique_count} unique</span>
            </div>
            <div className="mt-2 h-1 bg-[#F5F5F5] overflow-hidden">
              <div className="h-full bg-[#1E4D55]" style={{ width: `${(p.hits / maxHits) * 100}%` }} />
            </div>
          </div>
        ))}
        {(!data.top_pages || data.top_pages.length === 0) && <EmptyHint msg="No traffic in the last 24 hours yet." />}
      </div>

      {data.top_referrers?.length > 0 && (
        <>
          <h3 className="mt-6 text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mb-3">Top referrers</h3>
          <div className="space-y-1">
            {data.top_referrers.map((r) => (
              <div key={r.referrer} className="text-xs flex justify-between gap-3 bg-white border border-[#0A0A0A]/10 p-2 font-mono">
                <span className="truncate">{r.referrer}</span>
                <span className="text-[#475569] shrink-0">{r.hits}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {data.top_countries?.length > 0 && (
        <>
          <h3 className="mt-6 text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mb-3">Top countries</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="traffic-top-countries">
            {data.top_countries.map((c) => (
              <div key={c.country} className="bg-white border border-[#0A0A0A]/10 p-3 flex items-center justify-between">
                <span className="font-mono text-sm font-bold">{c.country}</span>
                <span className="text-xs text-[#475569]">{c.hits}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Funnel
// ---------------------------------------------------------------------------
function FunnelTab() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    apiClient.get("/admin/marketing/funnel")
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load funnel"));
  }, []);

  if (error) return <ErrorBox msg={error} />;
  if (!data) return <Loader2 size={20} className="animate-spin text-[#1E4D55]" />;
  const maxCount = Math.max(1, ...data.stages.map((s) => s.count));

  return (
    <div data-testid="funnel-panel" className="space-y-2">
      {data.stages.map((s, i) => {
        const width = Math.max(20, (s.count / maxCount) * 100);
        const dropOff = i > 0 ? 100 - s.of_prev_pct : null;
        return (
          <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-white border border-[#0A0A0A]/10 p-4 transition-all" style={{ marginLeft: `${(100 - width) / 2}%`, marginRight: `${(100 - width) / 2}%` }}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-display font-bold text-[#0A0A0A]">{s.label}</span>
                  <span className="font-display text-2xl font-black tracking-tighter text-[#0A0A0A]">{s.count.toLocaleString()}</span>
                </div>
                {i > 0 && (
                  <p className="mt-1 text-[11px] text-[#475569]">
                    {s.of_prev_pct.toFixed(1)}% of previous stage
                    {dropOff > 0 && <span className="ml-2 text-[#B91C1C]">· {dropOff.toFixed(1)}% drop-off</span>}
                  </p>
                )}
              </div>
            </div>
            {i < data.stages.length - 1 && (
              <div className="flex justify-center my-1"><ArrowDown size={14} className="text-[#475569]" /></div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Promo codes
// ---------------------------------------------------------------------------
function PromoCodesTab() {
  const [codes, setCodes] = React.useState([]);
  const [showCreate, setShowCreate] = React.useState(false);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/marketing/promo-codes");
      setCodes(data.promo_codes || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load promo codes");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const disable = async (id) => {
    if (!window.confirm("Disable this promo code? Existing redemptions are unaffected.")) return;
    await apiClient.post(`/admin/marketing/promo-codes/${id}/disable`);
    await load();
  };

  return (
    <div data-testid="promo-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#475569]">Promo codes apply a fixed-£ or % discount to auto-priced jobs at checkout.</p>
        <button onClick={() => setShowCreate(true)} data-testid="promo-create-button" className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold">
          <Plus size={14} /> New code
        </button>
      </div>
      {error && <ErrorBox msg={error} />}
      {loading && <Loader2 size={20} className="animate-spin text-[#1E4D55]" />}
      <div className="space-y-2" data-testid="promo-list">
        {codes.map((c) => (
          <div key={c.id} data-testid={`promo-row-${c.code}`} className={`bg-white border p-4 flex items-start justify-between gap-3 ${c.disabled ? "opacity-50" : ""} border-[#0A0A0A]/10`}>
            <div className="flex-1">
              <p className="font-mono font-bold text-lg text-[#0A0A0A]">{c.code}</p>
              <p className="mt-1 text-xs text-[#475569]">
                {c.percent_off ? `${c.percent_off}% off` : c.amount_off_pence ? `${gbp(c.amount_off_pence)} off` : "—"}
                {c.max_uses ? ` · ${c.uses}/${c.max_uses} used` : ` · ${c.uses} used`}
                {c.valid_until ? ` · expires ${c.valid_until}` : ""}
              </p>
              {c.notes && <p className="mt-1 text-[11px] text-[#475569] italic">{c.notes}</p>}
            </div>
            {!c.disabled && (
              <button onClick={() => disable(c.id)} className="text-[11px] font-semibold text-[#B91C1C] hover:underline">Disable</button>
            )}
            {c.disabled && <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">Disabled</span>}
          </div>
        ))}
        {codes.length === 0 && !loading && <EmptyHint msg="No promo codes yet — create your first one." />}
      </div>
      {showCreate && <CreatePromoModal onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await load(); }} />}
    </div>
  );
}

function CreatePromoModal({ onClose, onCreated }) {
  const [code, setCode] = React.useState("");
  const [percentOff, setPercentOff] = React.useState("");
  const [amountOff, setAmountOff] = React.useState("");
  const [maxUses, setMaxUses] = React.useState("");
  const [validUntil, setValidUntil] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await apiClient.post("/admin/marketing/promo-codes", {
        code: code.trim().toUpperCase(),
        percent_off: percentOff ? parseFloat(percentOff) : null,
        amount_off_pence: amountOff ? Math.round(parseFloat(amountOff) * 100) : null,
        max_uses: maxUses ? parseInt(maxUses) : null,
        valid_until: validUntil || null,
        notes: notes.trim() || null,
      });
      await onCreated();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create code");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-[#0A0A0A]/10 max-w-md w-full p-6 space-y-4" data-testid="promo-create-modal">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-xl font-bold tracking-tight text-[#0A0A0A]">New promo code</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[#F5F5F5]"><X size={16} /></button>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-1">Code</label>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required pattern="[A-Z0-9_-]{3,32}" placeholder="WELCOME20" data-testid="promo-code-input" className="w-full px-3 py-2 border border-[#0A0A0A]/15 font-mono uppercase focus:border-[#1E4D55] outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.14em] font-bold mb-1">% off</label>
            <input type="number" min="1" max="100" value={percentOff} onChange={(e) => { setPercentOff(e.target.value); setAmountOff(""); }} placeholder="20" data-testid="promo-percent-input" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.14em] font-bold mb-1">£ off</label>
            <input type="number" min="1" value={amountOff} onChange={(e) => { setAmountOff(e.target.value); setPercentOff(""); }} placeholder="50" data-testid="promo-amount-input" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.14em] font-bold mb-1">Max uses (optional)</label>
            <input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="100" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.14em] font-bold mb-1">Valid until (optional)</label>
            <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.14em] font-bold mb-1">Internal note (optional)</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Q1 launch campaign" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none text-sm" />
        </div>
        {error && <ErrorBox msg={error} />}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A]">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="promo-create-submit" className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4 — SEO audit
// ---------------------------------------------------------------------------
function SeoAuditTab() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const run = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get("/admin/marketing/seo-audit");
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Audit failed");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { run(); }, [run]);

  return (
    <div data-testid="seo-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#475569]">Fetches each public page server-side and audits title/meta/OG tags, H1s, images.</p>
        <button onClick={run} disabled={loading} data-testid="seo-rerun-button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E4D55] hover:text-[#163C42] disabled:opacity-50">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-run audit
        </button>
      </div>
      {error && <ErrorBox msg={error} />}
      {!data && loading && <Loader2 size={20} className="animate-spin text-[#1E4D55]" />}
      <div className="space-y-3">
        {data?.audits?.map((a) => <SeoAuditCard key={a.url} audit={a} />)}
      </div>
    </div>
  );
}

function SeoAuditCard({ audit }) {
  const score = audit.score ?? 0;
  const scoreColor = score >= 80 ? "#5BB247" : score >= 60 ? "#F5A623" : "#B91C1C";
  return (
    <div data-testid={`seo-audit-card`} className="bg-white border border-[#0A0A0A]/10 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1">
          <a href={audit.url} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-[#1E4D55] hover:underline inline-flex items-center gap-1">
            {audit.url} <ExternalLink size={11} />
          </a>
          {audit.title && <p className="mt-2 text-sm font-semibold text-[#0A0A0A]">{audit.title}</p>}
          {audit.description && <p className="mt-1 text-xs text-[#475569] line-clamp-2">{audit.description}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="font-display text-3xl font-black tracking-tighter" style={{ color: scoreColor }} data-testid="seo-score">{score}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#475569]">SEO score</p>
        </div>
      </div>
      {(audit.issues && audit.issues.length > 0) ? (
        <div className="mt-4 space-y-1.5">
          {audit.issues.map((issue, i) => (
            <div key={i} className="text-xs flex items-start gap-2">
              {issue.severity === "high" && <AlertCircle size={12} className="text-[#B91C1C] mt-0.5 shrink-0" />}
              {issue.severity === "medium" && <AlertTriangle size={12} className="text-[#A66800] mt-0.5 shrink-0" />}
              {issue.severity === "low" && <AlertTriangle size={12} className="text-[#475569] mt-0.5 shrink-0" />}
              <span className="text-[#475569]"><strong className="text-[#0A0A0A]">{issue.field}:</strong> {issue.message}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-xs text-[#2c8642] inline-flex items-center gap-1"><CheckCircle2 size={12} /> No issues found</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
function Stat({ label, value, accent }) {
  return (
    <div className={`p-4 border-r border-b border-[#0A0A0A]/10 ${accent ? "bg-[#1E4D55] text-white" : "bg-white"}`}>
      <p className={`text-[11px] uppercase tracking-[0.22em] font-bold ${accent ? "text-white/70" : "text-[#475569]"}`}>{label}</p>
      <p className="mt-1 font-display text-3xl font-black tracking-tighter">{value}</p>
    </div>
  );
}

function ErrorBox({ msg }) {
  return <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3"><AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{msg}</span></div>;
}

function EmptyHint({ msg }) {
  return <p className="text-xs text-[#475569] italic">{msg}</p>;
}
